/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import GenericButton from '@ui-components/Button/GenericButton'
import GenericDropdown from '@ui-components/Dropdown/GenericDropdown'
import { GenericTable } from '@ui-components/Table/GenericTable'
import React, { useEffect, useMemo, useState } from 'react'
import { ReactComponent as IconPlus } from "../../../../../shared/assets/Icons/IconPlusV3.svg";
import { getTenantById, getTenants, Partner, Tenant, TenantDetail } from '../request/request';
import { getPartnerColumns, PartnerTableRow } from './PartnersTableColumns';

import { useAuth0 } from '@auth0/auth0-react';
import GenericCard from '@ui-components/Card/GenericCard';
import GenericSearchbar, { ParsedFilter } from '@ui-components/Searchbar/GenericSearchbar';
import { buildPredicate, FilterField, labelId, parseToken } from 'kontrolEdge/helpers/advancedFilters';
import { SkeletonTableRows } from '@ui-components/Skeleton/GenericSkeleton';
import { PartnerEditModal, PartnerViewModal } from './PartnerModals';


export default function PartnerPage() {
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [partnersList, setPartnersList] = useState<PartnerTableRow[]>([]);
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const [isFetching, setIsFetching] = useState(false);

  // Modal states
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<TenantDetail | null>(null);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);

// ===== 2) FETCH TENANTS ON MOUNT =====
useEffect(() => {
if (isLoading) return;
if (!isAuthenticated) return;

const fetchTenants = async () => {
try {
  setIsFetching(true);
  const token = await getAccessTokenSilently();
  const resp = await getTenants(token);
  const rawData = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
  
  // Filter for PARTNER type (TenantType.id === 2)
  const partners = rawData.filter((tenant: Tenant) => 
    tenant.TenantType?.id === 2
  );
  
  // Map Tenant to PartnerTableRow
  const mappedPartners: PartnerTableRow[] = partners.map((tenant: Tenant) => ({
    id: tenant.id,
    displayName: tenant.nameDisplay || tenant.Name,
    name: tenant.Name,
    chargebeeCustomerId: tenant.chargebeeCustomerId,
  }));
  
  setPartnersList(mappedPartners);
} catch (err) {
  console.error("Error fetching tenants:", err);
} finally {
  setIsFetching(false);
}
};

fetchTenants();
}, [isLoading, isAuthenticated, getAccessTokenSilently]);

// ===== 2.1) FETCH PARTNER DETAIL =====
useEffect(() => {
if (!selectedPartnerId || !isAuthenticated) return;

const fetchPartnerDetail = async () => {
try {
  setIsModalLoading(true);
  const token = await getAccessTokenSilently();
  const resp = await getTenantById(token, selectedPartnerId);
  const partnerData = (resp as any)?.data?.data ?? (resp as any)?.data;
  setSelectedPartner(partnerData);
} catch (err) {
  console.error("Error fetching partner detail:", err);
} finally {
  setIsModalLoading(false);
}
};

fetchPartnerDetail();
}, [selectedPartnerId, isAuthenticated, getAccessTokenSilently]);

// ===== 3) HANDLERS =====
const handleViewPartner = (p: PartnerTableRow) => {
setSelectedPartnerId(p.id);
setIsViewModalOpen(true);
setSelectedPartner(null); // Clear previous data
};

const handleEditPartner = (p: PartnerTableRow) => {
setSelectedPartnerId(p.id);
setIsEditModalOpen(true);
setSelectedPartner(null); // Clear previous data
};

const handleDeleteClick = (p: PartnerTableRow) => {
console.log('deleting', p);
};

const handleAddPartner = () => {
setSelectedPartnerId(null);
setSelectedPartner(null);
setIsModalLoading(false); // No loading for new partner
setIsEditModalOpen(true);
};

const handleCloseViewModal = () => {
setIsViewModalOpen(false);
setSelectedPartnerId(null);
setSelectedPartner(null);
};

const handleCloseEditModal = () => {
setIsEditModalOpen(false);
setSelectedPartnerId(null);
setSelectedPartner(null);
};

const handleSavePartner = () => {
// TODO: Implement save logic
console.log('Saving partner:', selectedPartner);
handleCloseEditModal();
};

const handleFieldChange = (field: string, value: string | boolean) => {
if (!selectedPartner) return;
setSelectedPartner({
...selectedPartner,
[field]: value,
});
};

// ===== 4) FILTERING =====
function makePartnerFields(): FilterField<PartnerTableRow>[] {
return [
{ label: "ID", key: "id", type: "number", accessor: r => r.id },
{ label: "Partner Name", key: "partnername", type: "string", accessor: r => r.displayName, inFreeText: true },
{ label: "Name", key: "name", type: "string", accessor: r => r.name, inFreeText: true },
{ label: "Chargebee Customer ID", key: "chargebeecustomerid", type: "string", accessor: r => r.chargebeeCustomerId || "" },
];
}

const partnerFields = useMemo(() => makePartnerFields(), []);

const fieldById = useMemo(() => {
const m = new Map<string, FilterField<PartnerTableRow>>();
for (const f of partnerFields) {
const id = (f.key ?? labelId(f.label)).toLowerCase();
m.set(id, f);
}
return m;
}, [partnerFields]);

const keyMap = useMemo(() => {
const m = new Map<string, string>();
for (const f of partnerFields) {
const id = (f.key ?? labelId(f.label)).toLowerCase();
const label = f.label.toLowerCase();
m.set(id, id);
m.set(label, id);
}
return m;
}, [partnerFields]);

const normalizedFilters = useMemo(() => {
return activeFilters.map((f) => {
const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

if (token.key) {
  const k = token.key.trim().toLowerCase();
  token.key = keyMap.get(k) ?? labelId(token.key);
}

const field = token.key ? fieldById.get(token.key) : undefined;
if (field?.type === "string" && token.value) {
  const quoted = token.value.match(/^"(.*)"$/);
  if (quoted) {
    token.value = quoted[1];
    token.op = "=";
  } else if (!token.op) {
    token.op = ":"; // contains
  }
}
return token;
});
}, [activeFilters, keyMap, fieldById]);

const predicate = useMemo(
() => buildPredicate<PartnerTableRow>(normalizedFilters, partnerFields),
[normalizedFilters, partnerFields]
);

const filteredPartners = useMemo(
() => partnersList.filter(predicate),
[partnersList, predicate]
);

const suggestedKeys = useMemo(() => {
return partnerFields.map(f => f.label);
}, [partnerFields]);

//Safely pass them into getPartnerColumns:
const columns = getPartnerColumns({
handleViewPartner,
handleEditPartner,
handleDeleteClick,
});

return (
<GenericCard isCollapsible={false}>
{/* Header with Searchbar and Add Button */}
<div className="mb-4 p-5 bg-[var(--semantic-bg-primary)]">
  <div className="flex items-end justify-between">
    <div className="w-[520px] my-5">
      <GenericSearchbar
        placeholder="Find by attribute or value"
        suggestedKeys={suggestedKeys}
        onFiltersChange={setActiveFilters}
      />
    </div>
    {/* <div className="mb-10">
      <GenericButton
        label={
          <div className="flex items-center gap-2">
            <IconPlus className="w-5 h-5" />
            <span>Create new partner</span>
          </div>
        }
        variant="brand"
        size="md"
        onClick={handleAddPartner}
      />
    </div> */}
  </div>
</div>

{/* Table */}
<div className="px-5 mb-5">
  {isFetching ? (
    <div className="space-y-2">
      <SkeletonTableRows rows={5} cols={4} />
    </div>
  ) : (
    <GenericTable
      columns={columns}
      data={filteredPartners}
      width="100%"
      initialRowsPerPage={5}
      rowsPerPageOptions={[5, 10]}
      tableClassName="bg-semantic-bg-primary"
    />
  )}
</div>

{/* View Modal */}
<PartnerViewModal
  isOpen={isViewModalOpen}
  isLoading={isModalLoading}
  partner={selectedPartner}
  onClose={handleCloseViewModal}
/>

{/* Edit Modal */}
<PartnerEditModal
  isOpen={isEditModalOpen}
  isLoading={isModalLoading}
  partner={selectedPartner}
  onClose={handleCloseEditModal}
  onSave={handleSavePartner}
  onFieldChange={handleFieldChange}
/>
</GenericCard>
)
}
